import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectConfig } from 'redux/selectors/projectConfig';
import IconButton from 'shared/buttons/IconButton';
import { ButtonColor, ButtonType } from 'shared/buttons/models/ButtonModels';
import PrimaryButton from 'shared/buttons/PrimaryButton';
import { getPublishStatus, onPublish, onSave, getStatus } from 'shared/editor/PublishHelper';
import useToggle from 'shared/utils/useToggle';
import PublishSection from 'shared/wizard/PublishSection';
import { XIcon, ChevronDownIcon } from '@heroicons/react/outline';
import InputWidget from 'shared/widgets/InputWidget';
import { setAction } from 'redux/actions/projectConfig';
import { RootState } from 'redux/store';
import { getAggregatedOptions as getTableAggregatedOptions } from '../../TableEditorPage/selectors/tableEditor';
import { getAggregatedOptions as getLayoutAggregatedOptions } from '../../LayoutEditorPage/selectors/layoutEditor.js';
import PageDimmer from 'shared/wizard/components/PageDimmer';
import SvgIconButton from 'shared/buttons/SvgIconButton';
import { hideInlineDeleteButton } from 'shared/widgets/richtextgroup/DeleteButton';
import { checkPermission } from 'utils/ProfileHelpers';
import { useIsMobile } from 'shared/utils/mediaHelper';

export default () => {
  const dispatch = useDispatch();
  const projectConfig = useSelector(getProjectConfig);
  const {
    saved,
    published,
    publishLoading,
    changeMade,
    projectName,
    aggregatedOptions,
    lastPublishTime,
    lastSaved,
    isPastingActive
  } = projectConfig;
  const { type } = useSelector((state: RootState) => state.layoutEditorPage);
  const { isThemeEditor } = useSelector((state: RootState) => state.chartEditorPage);
  const { user, team } = useSelector((state: RootState) => state.profile);
  const [value, setValue] = useState(projectName);
  const [editing, toggleEditing] = useToggle(false);
  const [open, toggleOpen] = useToggle(false);
  const isMobile = useIsMobile();
  const { buttonText, light } = getPublishStatus(published, saved, changeMade, lastPublishTime, lastSaved);

  const anonymous = !user && !team;
  const Icon = open ? XIcon : ChevronDownIcon;
  const hasPublishPermission = anonymous || checkPermission('publish');
  const getSavePermission = () => {
    if (isThemeEditor) {
      return !anonymous && checkPermission('theme_management');
    }
    return anonymous || checkPermission('create_chart');
  };
  const hasSavePermission = getSavePermission();
  const inEditPhoneMode = editing && isMobile;

  let aggregated = aggregatedOptions;
  if (type === 'layout') aggregated = getLayoutAggregatedOptions(projectConfig);
  else if (type === 'table') aggregated = getTableAggregatedOptions(projectConfig);

  const toggleClassNames = classNames({
    'top-[-700px] max-h-0 h-0': !open,
    'top-[64px] h-[calc(100%-64px)] ev-sm:top-[80px] ev-sm:h-[calc(100%-80px)]': open
  });

  useEffect(() => {
    if (editing === false) {
      dispatch(
        setAction({
          projectName: value || aggregated?.title?.text || 'Untitled project',
          changeMade: true
        })
      );
    }
  }, [editing]);

  useEffect(() => {
    setValue(projectName);
  }, [projectName]);

  const togglePublishSection = () => {
    toggleOpen();
    hideInlineDeleteButton();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') toggleEditing();
  };

  const onSaveClick = () => {
    if (!open && !isThemeEditor) togglePublishSection();
    if (saved && !changeMade) return;
    onSave(type, isThemeEditor, dispatch);
  };

  const onPublishClick = () => {
    if (!open) togglePublishSection();
    onPublish(type, dispatch);
  };

  return (
    <>
      {open && <PageDimmer onClick={() => toggleOpen()} className={'opacity-0 z-[2]'} />}
      <div
        className={`flex justify-between w-full px-8 h-16 ev-sm:h-20 bg-ev-light-purple ${
          isPastingActive ? '' : 'z-[3]'
        }`}
      >
        <div className={`flex items-center ev-sm:w-2/5 ${inEditPhoneMode ? 'w-full' : 'w-[25%]'}`}>
          {!editing && (
            <div
              className="overflow-hidden whitespace-nowrap pr-1 text-ellipsis text-2xl italic underline text-ev-navy-blue-2 rounded border-2 border-transparent hover:bg-ev-baby-blue-2 focus:shadow-light"
              onClick={() => toggleEditing()}
            >
              {value}
            </div>
          )}
          {editing && (
            <InputWidget
              value={value}
              onChange={({ val }) => setValue(val)}
              onKeyDown={onKeyDown}
              className={`${inEditPhoneMode ? 'w-full' : 'w-72'}`}
            />
          )}
          <IconButton
            icon={editing ? 'turn-down-left' : 'pen'}
            onClick={() => toggleEditing()}
            className="shrink-0 ml-2 h-8 w-8 rounded"
            buttonColor={ButtonColor.Transparent}
          />
        </div>
        <div className="flex content-center">
          <div className="hidden lg:block py-2 pr-4 font-medium">{getStatus(lastPublishTime, lastSaved)}</div>

          {hasSavePermission && !inEditPhoneMode && (
            <PrimaryButton
              onClick={onSaveClick}
              text={saved && !changeMade ? 'Saved' : 'Save'}
              buttonColor={ButtonColor.White}
              buttonType={ButtonType.Info}
              disabled={saved && !changeMade}
              className="mr-2 text-sm"
            />
          )}

          {!isThemeEditor && !inEditPhoneMode && hasPublishPermission && (
            <>
              <PrimaryButton
                onClick={onPublishClick}
                useLoader={{
                  default: {
                    text: buttonText,
                    icon: 'circle-arrow-up'
                  },
                  loading: {
                    text: buttonText,
                    check: publishLoading
                  },
                  done: {
                    text: buttonText,
                    icon: 'rotate',
                    check: published
                  }
                }}
                buttonColor={light ? ButtonColor.White : ButtonColor.NavyBlue}
                buttonType={ButtonType.Info}
                className="text-sm"
              />

              <SvgIconButton
                Icon={Icon}
                width={24}
                height={24}
                onClick={togglePublishSection}
                buttonClasses="flex ml-2 w-8 h-8 rounded content-center"
                buttonColor={ButtonColor.Transparent}
              />
            </>
          )}
        </div>
      </div>
      <div
        className={`absolute right-0  ${
          isPastingActive ? '' : 'z-[2]'
        } bg-ev-light-purple w-full ev-sm:w-72 ${toggleClassNames} transition-all overflow-auto overflow-x-hidden`}
      >
        <PublishSection inEditor={true} />
      </div>
    </>
  );
};
